/**
 * ThrillingLocationsServiceModule — 2026-06-10 §3.F wire-up for the canonical
 * Thrilling Locations (Victory Games VG35010) venue-staff NPCs (39 across
 * casino / hotel / restaurant / train / boat / plane).
 *
 * Routes (per briefing_room_backend/routes/canonical_npcs_thrilling_locations_vg35010.py):
 *   GET /api/modules/briefing-room/canonical-npcs-thrilling-locations-vg35010        → ThrillingLocationsNpcManifest
 *   GET /api/modules/briefing-room/canonical-npcs-thrilling-locations-vg35010/{id}   → full entry | 404
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders().
 *
 * Distinct from VillainsServiceModule + CanonicalNpcsServiceModule — each
 * sourcebook keeps its own endpoint + drawer. The manifest carries a `venue`
 * per entry so the drawer can group by venue (6 tabs).
 */
import { getAuthHeaders } from './authHeaders';

export type ThrillingLocationsVenue =
  | 'casino'
  | 'hotel'
  | 'restaurant'
  | 'train'
  | 'boat'
  | 'plane';

export interface ThrillingLocationsNpcManifestEntry {
  id: string;
  name: string;
  category: string;
  venue: ThrillingLocationsVenue;
  manifest_section: string;
  source_md_line: number | null;
}

export interface ThrillingLocationsNpcManifest {
  npcs: ThrillingLocationsNpcManifestEntry[];
  total: number;
  /**
   * Additional VG35010 NPCs enumerated as pending authoring. All 39 venue NPCs
   * shipped in §3.B, so this is 0 unless a later sub-phase enqueues more.
   */
  pending_remaining_count: number;
  venues: ThrillingLocationsVenue[];
  schema_version: string;
  source: string;
}

const BASE_PATH =
  '/api/modules/briefing-room/canonical-npcs-thrilling-locations-vg35010';

export class ThrillingLocationsServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  async list(): Promise<ThrillingLocationsNpcManifest> {
    const resp = await this.fetchFn(BASE_PATH, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(`ThrillingLocationsServiceModule: list HTTP ${resp.status}`);
    }
    return (await resp.json()) as ThrillingLocationsNpcManifest;
  }

  async get(npcId: string): Promise<Record<string, unknown> | null> {
    const resp = await this.fetchFn(`${BASE_PATH}/${encodeURIComponent(npcId)}`, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (resp.status === 404) {
      return null;
    }
    if (!resp.ok) {
      throw new Error(`ThrillingLocationsServiceModule: get HTTP ${resp.status}`);
    }
    return (await resp.json()) as Record<string, unknown>;
  }
}
